import {
  compactOpenerThemeForKind,
  OPENER_KIND_INFO,
  OPENER_KINDS,
  type OpenerColorRole,
  type OpenerKind,
  type ShopifyOpenerThemeDraft,
  shopifyOpenerThemeDraftOf,
} from "@loot-os/api-contract";
import { type LootTheme, resolveTheme } from "@loot-os/react";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { type ReactNode, useState } from "react";
import type { OpenerStyleChoice, OpenerStyleValues } from "../lib/openerStyles";
import { OpenerStylePreview } from "./OpenerStylePreview";
import { OpenerThemeEditor } from "./OpenerThemeEditor";

const KIND_ITEMS = Object.fromEntries(
  OPENER_KINDS.map((kind) => [kind, OPENER_KIND_INFO[kind].label]),
);

/**
 * The opener style editor, a page of its own: the style's name, its animation and the theme
 * tokens it overrides (`OpenerThemeEditor`), beside the real opener previewing the result over
 * `hostTheme`. Mount it with a `key` per style so the form starts from that style.
 */
export function OpenerStyleEditor({
  style,
  hostTheme,
  logoSvg,
  brandName,
  hostLabel,
  crumb,
  saving,
  onSave,
  onCancel,
}: {
  /** null = a new style. */
  style: OpenerStyleChoice | null;
  hostTheme: LootTheme | undefined;
  /** The environment's opener logo, which the preview wears; omitted in the platform scope. */
  logoSvg?: string;
  /** The environment's opener brand name, which the preview prints without a logo. */
  brandName?: string;
  /** What an unset token follows, as the description names it. */
  hostLabel: string;
  /** The link back to the list, above the title, for a page with no header trail. */
  crumb?: ReactNode;
  saving: boolean;
  onSave: (values: OpenerStyleValues) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(style?.name ?? "");
  const [kind, setKind] = useState<OpenerKind>(style?.kind ?? "spinner");
  const [draft, setDraft] = useState<ShopifyOpenerThemeDraft>(
    shopifyOpenerThemeDraftOf(style?.theme ?? null),
  );
  // `{}` = every token follows the host.
  const theme = compactOpenerThemeForKind(draft, kind) ?? {};
  const host = resolveTheme(hostTheme);
  const fallbackFor = (role: OpenerColorRole) => host.colors[role];

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ name: name.trim(), kind, theme });
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {crumb && <div className="mb-1 text-sm">{crumb}</div>}
          <h1 className="text-2xl font-semibold tracking-tight">
            {style ? `Edit ${style.name}` : "New opener style"}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            The animation a box opens with and the colors it overrides. Every field left blank
            follows {hostLabel}, so the opener matches wherever it is shown.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button type="button" variant="outline" disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving || name.trim() === ""}>
            {style ? "Save" : "Create"}
          </Button>
        </div>
      </div>

      <OpenerThemeEditor
        idPrefix="opener-style"
        kind={kind}
        draft={draft}
        onChange={setDraft}
        fallbackFor={fallbackFor}
        fontPlaceholder="The host's own font"
        disabled={saving}
        aside={
          <Card>
            <CardHeader>
              <CardTitle>Style</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="grid content-start gap-2">
                <Label htmlFor="opener-style-name">Name</Label>
                <Input
                  id="opener-style-name"
                  value={name}
                  maxLength={60}
                  required
                  disabled={saving}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="grid content-start gap-2">
                <Label htmlFor="opener-style-kind">Animation</Label>
                <Select
                  value={kind}
                  items={KIND_ITEMS}
                  disabled={saving}
                  onValueChange={(next) => next && setKind(next as OpenerKind)}
                >
                  <SelectTrigger id="opener-style-kind" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OPENER_KINDS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {OPENER_KIND_INFO[option].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {OPENER_KIND_INFO[kind].description}
                </p>
              </div>
            </CardContent>
          </Card>
        }
        preview={
          <OpenerStylePreview
            kind={kind}
            hostTheme={hostTheme}
            overrides={theme}
            logoSvg={logoSvg}
            brandName={brandName}
          />
        }
      />
    </form>
  );
}
